import PropTypes from 'prop-types';
import React from 'react';
import MaskedInput from 'react-text-mask'
import Popup from 'reactjs-popup';
import Tippy from '@tippyjs/react';
import { toast } from 'react-toastify';

import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import {
  SETTLEMENT_OVERRIDABLE_TYPES,
  canEditSettlementRow,
  isEditingItemSettlementLineItem,
  settlementLineItemToEdit,
} from '../../../shared/settlementRowEditing';
import {
  TAX_WITHHOLDING_EXEMPT_COLOR,
  TAX_WITHHOLDING_EXEMPTION_ICON,
  TAX_WITHHOLDING_EXEMPTION_LABEL,
  TAX_WITHHOLDING_EXEMPTION_TEST_ID,
  canToggleSettlementLineItemExemption,
  showsNotWithheld,
  taxWithholdingExemptionTooltip,
} from '../../../shared/taxWithholdingExemption';

const axios = require("axios").default;

function isEditingSettlementOverride(item, itemToEdit) {
  return (
    itemToEdit
      && itemToEdit.overridable_type
      && itemToEdit.overridable_type === item.overridable_type
      && itemToEdit.overridable_id
      && itemToEdit.overridable_id === item.overridable_id
      && itemToEdit.calculatedLeftHand === item.calculatedLeftHand
      && itemToEdit.section === item.section
      && SETTLEMENT_OVERRIDABLE_TYPES.includes(itemToEdit.overridable_type)
  )
}

function canDeleteRow(item, itemToEdit) {
  return (
    isEditingItemSettlementLineItem(item, itemToEdit)
      || (item.settlementOverride && item.settlementOverride.id)
  );
}

const inlineMenuContentStyle = {
  textAlign: "left",
  border: "none",
  borderRadius: "8px",
  boxShadow: "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  width: "150px",
  padding: "0px",
  background: "var(--sl-bg-primary)"
};

const editInputStyle = {
  "fontSize": "12px",
  "border": "1px solid var(--od-c-e0e0e0, #E0E0E0)",
  "width": "100%",
  "padding": "2px 5px",
  "background": "var(--od-c-ffffff, #fff)",
  "color": "var(--od-c-000000, #000)"
};

const listButtonStyle = {
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "0px",
  textAlign: "left",
  width: "100%",
};

const rowPillStyle = (color, background, border) => ({
  display: "inline-block",
  marginLeft: "8px",
  padding: "1px 6px",
  fontSize: "10px",
  fontWeight: 600,
  color,
  background,
  border: `1px solid ${border}`,
  borderRadius: "3px",
  verticalAlign: "middle"
});

const exemptionToggleStyle = (active) => ({
  marginLeft: "8px",
  width: "22px",
  height: "22px",
  padding: "0px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  verticalAlign: "middle",
  fontSize: "12px",
  background: active ? TAX_WITHHOLDING_EXEMPT_COLOR : "transparent",
  color: active ? "var(--od-c-ffffff, #FFFFFF)" : "var(--od-c-d5d7da, #D5D7DA)"
});

const BudgetLineItemsTableRow = ({
  title,
  formatCurrency,
  numberWithCommas,
  csrfToken,
  team,
  offer,
  itemToEdit,
  itemToEditChanged,
  item,
  deleteSettlementLineItem,
  deleteSettlementOverride,
  isSettling,
  isV3,
  requestFormat,
  offerId,
  setBudgetState,
  editedDropCountChanged,
  showCurrencyConversion,
  globalCurrency,
  showQuantityInput = true,
  namePlaceholder,
  amountPlaceholder,
  onToggleTaxWithholdingExemption
}) => {
  const { settlementVisibility } = item;

  const showInSettlement = !!settlementVisibility
    ? settlementVisibility.visible
    : item.show_in_offer;

  const exemptFromTaxWithholding = !!item.exempt_from_tax_withholding;
  const canToggleExemption = !!onToggleTaxWithholdingExemption
    && isSettling
    && requestFormat === "html"
    && showInSettlement
    && canToggleSettlementLineItemExemption(item, title, offer);

  const toggleShowInSettlement = (shouldShow) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    if (!!settlementVisibility) {
      axios.patch("/teams/" + team.id + "/settlement_visibilities/" + (isV3 ? "v3/" : "") + settlementVisibility.id, {
        [isV3 ? "v3_settlement_visibility" : "settlement_visibility"]: {
          visible: shouldShow,
        }
      }).then(data => {
        setBudgetState((current) => {
          return {
            ...current,
            settlement_visibilities: current.settlement_visibilities.map((visibility) => {
              if (visibility.id === settlementVisibility.id) {
                return data.data;
              } else {
                return visibility;
              }
            })
          };
        });
      }).catch(err => {
        toast.error(err.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
        console.log(err);
      });
    } else {
      var originalAmount = item.settlementOverride ? item.settlementOverride.amount : item.left_hand;

      var visibilityParams = {
        amount: originalAmount,
        section: item.section,
        settleable_type: item.overridable_type,
        settleable_id: item.overridable_id,
        visible: shouldShow,
      };
      visibilityParams[isV3 ? "v3_offer_id" : "offer_id"] = offerId;

      axios.post("/teams/" + team.id + "/settlement_visibilities/" + (isV3 ? "v3/" : ""), {
        [isV3 ? "v3_settlement_visibility" : "settlement_visibility"]: visibilityParams
      }).then(data => {
        setBudgetState((current) => {
          return {
            ...current,
            settlement_visibilities: [
              ...current.settlement_visibilities,
              data.data
            ]
          };
        });
      }).catch(err => {
        toast.error(err.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
    }
  }

  return (
    <tr>
      {isEditingItemSettlementLineItem(item, itemToEdit) || isEditingSettlementOverride(item, itemToEdit) ? (
        <>
          <td style={{"padding": "3px 9px"}}>
            <input type="text"
                    style={editInputStyle}
                    autoFocus={true}
                    onChange={
                      (e) => {
                        itemToEditChanged({
                          ...itemToEdit,
                          name: e.target.value
                        });
                      }
                    }
                    placeholder={namePlaceholder}
                    value={itemToEdit.name || ""} />
          </td>
          <td style={{"textAlign": "right", "padding": "3px 9px"}}>
            {showQuantityInput ? (
              <MaskedInput style={{...editInputStyle, "textAlign": "right"}}
                            mask={item.calc_type === "fixed" ? (
                              createNumberMask({
                                prefix: "",
                                decimalLimit: '0'
                              })
                            ) : (
                              createNumberMask({
                                prefix: "",
                                allowDecimal: true,
                                decimalLimit: '5'
                              })
                            )}
                            onChange={
                              (e) => {
                                  itemToEditChanged({
                                    ...itemToEdit,
                                    quantity: e.target.value
                                  });
                              }
                            }
                            value={itemToEdit.quantity || ""} />
            ) : (
              <span>&nbsp;</span>
            )}
          </td>
          <td style={{"textAlign": "right", "padding": "3px 9px"}}>
            <CurrencyInput style={{...editInputStyle, "textAlign": "right"}}
                          placeholder={amountPlaceholder}
                          value={itemToEdit.amount || ""}
                          onChange={
                            (e) => {
                                itemToEditChanged({
                                  ...itemToEdit,
                                  amount: e.target.value
                                });
                            }
                          }
                          maskOptions={item.calc_type === "fixed" ? ({
                            prefix: "",
                            integerLimit: null
                          }) : ({
                            prefix: "",
                            allowDecimal: true,
                            decimalLimit: '5'
                          })} />
          </td>
          {showCurrencyConversion ? (
            <td style={{"textAlign": "right", "padding": "3px 9px"}}>
              {globalCurrency.formatted(item.calculatedTotal)}
            </td>
          ) : null}
          <td style={{"textAlign": "right", "padding": "3px 15px 3px 0px"}}>
            <strong>
              {formatCurrency(item.calculatedTotal)}
            </strong>
          </td>
          <td style={{"textAlign": "center", "padding": "3px 0px"}}>
            <a href="#"
                style={{"color": "var(--od-c-999999, #999999)", "marginRight": "3px"}}
                title={"Cancel"}
                onClick={
                  (e) => {
                    e.preventDefault();
                    itemToEditChanged({});
                  }
                }>
              <i aria-hidden className="fas fa-times-circle fa-fw"></i>
            </a>
            {canDeleteRow(item, itemToEdit) ? (
              <a href="#"
                  style={{"color": "var(--od-c-ff595e, #FF595E)", "marginRight": "3px"}}
                  title={isEditingItemSettlementLineItem(item, itemToEdit) ? "Delete" : "Delete Override"}
                  onClick={
                    (e) => {
                      e.preventDefault();

                      if(isEditingItemSettlementLineItem(item, itemToEdit)){
                        deleteSettlementLineItem(csrfToken, team, item);
                      } else {
                        deleteSettlementOverride(csrfToken, team, item.settlementOverride);
                      }
                    }
                  }>
                <i aria-hidden className="fas fa-trash-alt fa-fw"></i>
              </a>
            ) : null}
            <button style={{
                      "border": "none",
                      "cursor": "pointer",
                      "padding": "0px",
                      "backgroundColor": "transparent"
                    }}
                    title={"Save"}
                    type="submit">
              <i aria-hidden
                  style={{"color": "var(--od-c-1982c4, #1982C4)"}}
                  className="fas fa-check-circle fa-fw"></i>
            </button>
          </td>
        </>
      ) : (
        <>
          <td style={{"padding": "3px 0px 3px 8px"}}>
            {item.name && item.name.length > 0 ? (
              <span>
                {item.name}
              </span>
            ) : (
              <span style={{"opacity": "0.5"}}>
                --
              </span>
            )}
            {item.deducted_before_split && offer?.revenue_base === "adjusted_gross" ? (
              <span style={rowPillStyle("#9a6b00", "#fff8c5", "#d4a72c")}>
                Deducted before split
              </span>
            ) : null}
            {canToggleExemption ? (
              <Tippy content={taxWithholdingExemptionTooltip(exemptFromTaxWithholding)}>
                <button type="button"
                        data-testid={TAX_WITHHOLDING_EXEMPTION_TEST_ID}
                        aria-label={TAX_WITHHOLDING_EXEMPTION_LABEL}
                        aria-pressed={exemptFromTaxWithholding}
                        style={exemptionToggleStyle(exemptFromTaxWithholding)}
                        onClick={(event) => {
                          event.preventDefault();
                          onToggleTaxWithholdingExemption(csrfToken, team, item, !exemptFromTaxWithholding);
                        }}>
                  <i aria-hidden className={TAX_WITHHOLDING_EXEMPTION_ICON}></i>
                </button>
              </Tippy>
            ) : null}
            {showInSettlement && showsNotWithheld(item, title, offer) ? (
              <span style={rowPillStyle("#1a7f37", "#dafbe1", "#4ac26b")}>
                Not withheld
              </span>
            ) : null}
            {item.settlementOverride && (
              <Tippy content="This row has been manually overridden and is no longer automatically syncing">
                <span className="badge">
                  <i aria-hidden className="text-muted far fa-pencil fa-fw ml-1"></i>
                </span>
              </Tippy>
            )}
            {showInSettlement || (
              <Tippy content="Hidden from settlement PDF">
                <span className="badge">
                  <i aria-hidden className="text-muted fas fa-eye-slash fa-fw ml-1"></i>
                </span>
              </Tippy>
            )}
          </td>
          {!isSettling && title === "Tickets" ? (
            <>
              <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
                {numberWithCommas(item.available || 0)}
              </td>
              <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
                {numberWithCommas(item.comps || 0)}
              </td>
              <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
                {numberWithCommas(item.kills || 0)}
              </td>
            </>
          ) : null}
          <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
              {["fixed", "per_tickets_sold"].includes(item.calc_type) ? (
                numberWithCommas(item.calculatedRightHand)
              ) : (
                formatCurrency(item.calculatedRightHand)
              )}
          </td>
          <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
            {item.left_hand_text_override && item.left_hand_text_override.length > 0 ? (
              item.left_hand_text_override
            ) : ["fixed", "per_tickets_sold"].includes(item.calc_type) ? (
              formatCurrency(item.calculatedLeftHand)
            ) : (
              `${item.calculatedLeftHand}\%`
            )}
          </td>
          {showCurrencyConversion ? (
            <td style={{"textAlign": "right", "padding": "3px 9px 3px 0px"}}>
              {globalCurrency.formatted(item.calculatedTotal)}
            </td>
          ) : null}
          <td style={{"textAlign": "right", "padding": "3px 15px 3px 0px"}}>
            <strong>
              {formatCurrency(item.calculatedTotal)}
            </strong>
          </td>
          <td style={{"textAlign": "center", "padding": "3px 0px"}}>
            {isSettling && requestFormat === "html" && canEditSettlementRow(item) ? (
              <Popup
                trigger={
                  <a href="#"
                      className="text-muted">
                    <i aria-hidden className="fas fa-ellipsis-v"></i>
                  </a>
                }
                onOpen={
                  (e) => {
                    e.preventDefault();
                  }
                }
                arrow={false}
                offsetY={5}
                position="bottom right"
                contentStyle={inlineMenuContentStyle}
                closeOnDocumentClick
              >
                {close => (
                  <div>
                    <div className="row text-left">
                      <div className="col">
                        <ul className="list-group offer-inline-menu highlight-primary">
                          <li className="list-group-item">
                            <button
                              style={listButtonStyle}
                              onClick={
                                (e) => {
                                  e.preventDefault();

                                  var itemToEdit;

                                  if(item.overridable_type === "SettlementLineItem") {
                                    itemToEdit = settlementLineItemToEdit(item);
                                  } else {
                                    itemToEdit = Object.assign({}, item, {
                                      amount: item.calculatedLeftHand,
                                      quantity: item.calculatedRightHand
                                    });
                                  }

                                  itemToEditChanged(itemToEdit);
                                  editedDropCountChanged(null);
                                  close();
                                }
                              }
                              className="text-dark"
                            >
                              <i aria-hidden className="text-muted fas fa-pencil fa-fw mx-2"></i>
                              <span style={{"fontWeight": 'normal'}}>
                                Edit
                              </span>
                            </button>
                          </li>
                          <li className="list-group-item">
                            <button
                              style={listButtonStyle}
                              onClick={(e) => {
                                e.preventDefault();
                                toggleShowInSettlement(!showInSettlement);
                                close();
                              }}
                            >
                              <i aria-hidden className="text-muted fas fa-eye-slash fa-fw mx-2"></i>
                              <span style={{"fontWeight": 'normal'}}>
                                {showInSettlement ? "Hide from PDF" : "Show in PDF"}
                              </span>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </Popup>
            ) : (
              <span>&nbsp;</span>
            )}
          </td>
        </>
      )}
    </tr>
  );
};

BudgetLineItemsTableRow.propTypes = {
  title: PropTypes.string.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  numberWithCommas: PropTypes.func.isRequired,
  csrfToken: PropTypes.string,
  team: PropTypes.object.isRequired,
  itemToEdit: PropTypes.object,
  itemToEditChanged: PropTypes.func.isRequired,
  item: PropTypes.object.isRequired,
  deleteSettlementLineItem: PropTypes.func.isRequired,
  deleteSettlementOverride: PropTypes.func.isRequired,
  isSettling: PropTypes.bool.isRequired,
  isV3: PropTypes.bool,
  requestFormat: PropTypes.string,
  offerId: PropTypes.string.isRequired,
  setBudgetState: PropTypes.func.isRequired,
  editedDropCountChanged: PropTypes.func.isRequired,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  showQuantityInput: PropTypes.bool,
  namePlaceholder: PropTypes.string,
  amountPlaceholder: PropTypes.string,
  onToggleTaxWithholdingExemption: PropTypes.func
};

export default BudgetLineItemsTableRow;
